// src/components/RenterRequirementsBanner.js
//
// Tells a court renter what the club is still missing before they can book,
// as soon as they are in the app rather than at the moment they press Book.
//
// createMemberBooking is what actually enforces this (see the renter gate
// there). This is the courtesy half: the same two requirements, said early,
// and both settleable right here — the waiver by signing it, the insurance by
// the same attestation the renter step of registration takes. Choosing a court
// and a time and only then being refused is a poor way to learn a rule.
//
// Renders nothing at all for everyone else, and nothing for a renter who is
// already clear, so it costs a signed-in non-renter one prop check.

import React, { useEffect, useState, useCallback } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { db, auth } from '../firebase';
import { hasValidWaiverAcceptance } from '../utils/waiverGate';
import { resolveMember, canonicalUid } from '../utils/resolveMember';
import WaiverDialog from './WaiverDialog';

export default function RenterRequirementsBanner({ isRenter, insured }) {
  const [waiverDoc, setWaiverDoc] = useState(null);
  const [needsWaiver, setNeedsWaiver] = useState(false);
  const [checked, setChecked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [insuranceDone, setInsuranceDone] = useState(false);
  const [savingInsurance, setSavingInsurance] = useState(false);

  const check = useCallback(async () => {
    const user = auth.currentUser;
    if (!isRenter || !user) { setChecked(true); return; }
    try {
      const settings = await getDoc(doc(db, 'settings', 'membershipTiers'));
      const waiverId = settings.exists() ? (settings.data().requiredWaiverId || '') : '';
      if (!waiverId) { setNeedsWaiver(false); setChecked(true); return; }

      const wd = await getDoc(doc(db, 'clubDocuments', waiverId));
      if (!wd.exists() || wd.data().enabled === false) {
        // Same fail-open the server takes: a missing or disabled document is an
        // admin's unfinished setting, not a reason to nag.
        setNeedsWaiver(false); setChecked(true); return;
      }
      const record = { id: wd.id, ...wd.data() };
      setWaiverDoc(record);

      // Mirrors renterHasWaiver on the server: a renter who self-registered
      // ticked this same agreement at signup and carries waiverSigned instead
      // of a documentAcceptances entry. Nagging them for something they did —
      // and that the booking call would accept — is worse than not asking.
      if (await hasValidWaiverAcceptance(user.uid, record)) { setNeedsWaiver(false); setChecked(true); return; }
      // Canonical: the server checks waiverSigned and liabilityInsuranceConfirmed
      // on the same doc when it decides whether to accept a booking, so reading
      // the pointer here told a renter they were fine right up until the booking
      // call refused them.
      const { data: u } = await resolveMember(db, user);
      const days = Number(record.expirationDays) || 0;
      const signedMs = u?.waiverDate?.toMillis?.() ?? (u?.waiverDate ? new Date(u.waiverDate).getTime() : null);
      const registrationCounts = u?.waiverSigned === true
        && (days <= 0 || (signedMs && (Date.now() - signedMs) / 86400000 < days));
      setNeedsWaiver(!registrationCounts);
    } catch (e) {
      // Never invent a requirement the server might not agree with — a failed
      // read here shows nothing, and the booking call still enforces the rule.
      console.warn('Renter requirement check failed:', e);
      setNeedsWaiver(false);
    } finally {
      setChecked(true);
    }
  }, [isRenter]);

  useEffect(() => { check(); }, [check]);

  const accept = async (entry) => {
    const user = auth.currentUser;
    if (!user || !waiverDoc) throw new Error('Session expired. Please refresh and try again.');
    // Written where the booking call reads it. Rules allow this: an alias may
    // update the doc its own pointer names.
    await updateDoc(doc(db, 'users', await canonicalUid(db, user.uid)), {
      [`documentAcceptances.${waiverDoc.id}`]: entry,
    });
    setDialogOpen(false);
    setNeedsWaiver(false);
  };

  const confirmInsurance = async () => {
    const user = auth.currentUser;
    if (!user || savingInsurance) return;
    setSavingInsurance(true);
    try {
      // The same attestation the renter step of Register.js takes, in the same
      // fields — admin-recorded and self-attested differ only in the source.
      await updateDoc(doc(db, 'users', await canonicalUid(db, user.uid)), {
        liabilityInsuranceConfirmed: true,
        liabilityInsuranceDate: serverTimestamp(),
        liabilityInsuranceSource: 'self_attested',
      });
      setInsuranceDone(true);
    } catch (e) {
      console.warn('Could not record the insurance confirmation:', e);
    } finally {
      setSavingInsurance(false);
    }
  };

  const needsInsurance = isRenter && !insured && !insuranceDone;
  if (!isRenter || !checked || (!needsWaiver && !needsInsurance)) return null;

  const waiverName = waiverDoc?.name || 'liability waiver';

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
      <Alert severity="warning" sx={{ alignItems: 'center' }}
        action={needsWaiver ? (
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={() => setDialogOpen(true)}>
            Review and sign
          </Button>
        ) : null}>
        <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
          Before you can book court time
        </AlertTitle>
        {needsWaiver && (
          <Typography variant="body2">
            Your {waiverName} isn’t signed yet.
          </Typography>
        )}
        {needsInsurance && (
          <FormControlLabel
            sx={{ mt: needsWaiver ? 0.5 : 0, alignItems: 'flex-start' }}
            control={
              <Checkbox size="small" checked={false} disabled={savingInsurance}
                onChange={confirmInsurance} sx={{ pt: 0.25 }} />
            }
            label={
              <Typography variant="body2">
                I carry my own general liability insurance covering the activities
                I run on the club’s courts, and the club may ask to see a
                certificate at any time.
              </Typography>
            }
          />
        )}
      </Alert>

      <WaiverDialog
        open={dialogOpen}
        doc={waiverDoc}
        onClose={() => setDialogOpen(false)}
        onAccepted={accept}
      />
    </Box>
  );
}
